package com.flowyride.core.api

import com.flowyride.core.domain.model.GeoPoint
import com.flowyride.core.domain.place.NewSavedPlace
import com.flowyride.core.domain.place.SavedPlace
import com.flowyride.core.domain.place.SavedPlaceEdit
import kotlinx.serialization.Serializable

/** Addresses saved on the signed-in rider's account. */
object SavedPlacesApi {
    /** GET → [SavedPlaceDto] list, oldest first. POST [NewSavedPlaceDto] → 201 [SavedPlaceDto]. */
    const val PATH = "/api/places"

    /** PATCH [UpdateSavedPlaceDto] → [SavedPlaceDto]. DELETE → 204. */
    fun item(id: Long) = "$PATH/$id"
}

/** @param color "#RRGGBB". */
@Serializable
data class SavedPlaceDto(
    val id: Long,
    val name: String,
    val detail: String? = null,
    val lat: Double,
    val lon: Double,
    val emoji: String? = null,
    val color: String? = null,
)

@Serializable
data class NewSavedPlaceDto(
    val name: String,
    val detail: String? = null,
    val lat: Double,
    val lon: Double,
    val emoji: String? = null,
    val color: String? = null,
)

/** Replaces the name, emoji and color (a missing emoji or color removes it). */
@Serializable
data class UpdateSavedPlaceDto(val name: String, val emoji: String? = null, val color: String? = null)

fun SavedPlace.toDto() = SavedPlaceDto(id, name, detail, point.latitude, point.longitude, emoji, color?.toHexColor())

/** @throws IllegalArgumentException on invalid coordinates or color. */
fun SavedPlaceDto.toDomain() = SavedPlace(id, name, detail, GeoPoint(lat, lon), emoji, color?.parseHexColor())

fun NewSavedPlace.toDto() = NewSavedPlaceDto(name, detail, point.latitude, point.longitude, emoji, color?.toHexColor())

/** @throws IllegalArgumentException on invalid coordinates or color. */
fun NewSavedPlaceDto.toDomain() = NewSavedPlace(
    name = name.trim(),
    detail = detail?.trim()?.takeIf { it.isNotEmpty() },
    point = GeoPoint(lat, lon),
    emoji = emoji.cleanEmoji(),
    color = color?.parseHexColor(),
)

fun SavedPlaceEdit.toDto() = UpdateSavedPlaceDto(name, emoji, color?.toHexColor())

/** @throws IllegalArgumentException on an invalid color. */
fun UpdateSavedPlaceDto.toDomain() = SavedPlaceEdit(name.trim(), emoji.cleanEmoji(), color?.parseHexColor())

private fun String?.cleanEmoji(): String? = this?.trim()?.takeIf { it.isNotEmpty() }

/** 0xRRGGBB → "#RRGGBB". */
fun Int.toHexColor(): String = "#" + toString(16).padStart(6, '0').uppercase()

/** "#RRGGBB" → 0xRRGGBB. @throws IllegalArgumentException otherwise. */
fun String.parseHexColor(): Int {
    require(length == 7 && startsWith('#')) { "Color: #RRGGBB" }
    return substring(1).toIntOrNull(16) ?: throw IllegalArgumentException("Color: #RRGGBB")
}
